import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import {
    Drawer,
    Button,
    Select,
    Switch,
    Spin,
    Tag,
    Typography,
    notification,
} from "antd";
import {
    CloseOutlined,
    CheckCircleOutlined,
    ReloadOutlined,
    CloudDownloadOutlined,
    DeleteOutlined,
    ArrowUpOutlined,
    ArrowLeftOutlined,
    CloudUploadOutlined,
    SafetyCertificateOutlined,
    SwapOutlined,
    LogoutOutlined,
    LoginOutlined,
    KeyOutlined,
    DisconnectOutlined,
} from "@ant-design/icons";
import Axios from "axios";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { authApp } from "config";
import { fetchBackup, performBackup } from "utils/Firebase/backup";
import { runWithCloudHydrationGuard } from "utils/Firebase/autoSync";
import { updateBackedupPortfolios } from "store/Actions/portfolio";
import { updateBackedupAssets } from "store/Actions/asset";
import { updateSettings, updateLastBackup } from "store/Actions/settings";
import AppShell, { AutoSyncBadge } from "Components/Layout/AppShell";
import AddBinance from "Components/Forms/AddBinance";
import LoginWall from "Components/Shared/LoginWall";
import {
    mpTapLoginCTA,
    mpTapLogoutCTA,
    mpTapOverrideCTA,
    mpTapLocalOverrideCTA,
    mpTapChangeCurrency,
} from "Shared/Mixpanel/Portfolios";
import { Tokens } from "Shared/Tokens";
import { ThemeSwitcher } from "Shared/ThemeSwitcher";
import CoinGeckoLogo from "utils/images/CoinGeckoLogo.png";
import {
    APP_VERSION,
    checkAppVersion,
    applyAppUpdate,
    clearCacheAndReload,
} from "utils/appVersion";

dayjs.extend(duration);
dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const CURRENCY_OPTIONS = [
    { label: "USDT", value: "USDT" },
    { label: "USD", value: "USD" },
    { label: "INR", value: "INR" },
    { label: "EUR", value: "EUR" },
    { label: "GBP", value: "GBP" },
    { label: "BTC", value: "BTC" },
    { label: "ETH", value: "ETH" },
    { label: "BNB", value: "BNB" },
];

const SORT_OPTIONS = [
    { label: "Current value", value: "currentValue" },
    { label: "Invested amount", value: "investment" },
    { label: "ROI %", value: "roi" },
    { label: "Price", value: "price" },
    { label: "Quantity", value: "quantity" },
    { label: "Name", value: "name" },
];

const TIME_RANGE_OPTIONS = [
    { label: "1d", value: 1 },
    { label: "7d", value: 7 },
    { label: "30d", value: 30 },
    { label: "90d", value: 90 },
    { label: "180d", value: 180 },
    { label: "1y", value: 365 },
    { label: "All time", value: "all" },
];

const CUTOFF_PRESET_OPTIONS = [
    { label: "Since Jan 2026", value: "2026-01-01" },
    { label: "Cycle · Aug 24, 2026", value: "2026-08-24" },
    { label: "Since Oct 2026", value: "2026-10-01" },
    { label: "Since Jan 2025", value: "2025-01-01" },
    { label: "All time", value: "all" },
];

const TIMEZONE_OPTIONS = [
    { label: "IST (Asia/Kolkata)", value: "Asia/Kolkata" },
    { label: "UTC (Coordinated)", value: "UTC" },
    { label: "ET (America/New_York)", value: "America/New_York" },
    { label: "PT (America/Los_Angeles)", value: "America/Los_Angeles" },
    { label: "GMT (Europe/London)", value: "Europe/London" },
    { label: "CET (Europe/Berlin)", value: "Europe/Berlin" },
    { label: "SGT (Asia/Singapore)", value: "Asia/Singapore" },
    { label: "JST (Asia/Tokyo)", value: "Asia/Tokyo" },
    { label: "GST (Asia/Dubai)", value: "Asia/Dubai" },
];

const cardStyle = {
    backgroundColor: Tokens.card,
    border: `1px solid ${Tokens.hairline}`,
    borderRadius: Tokens.radiusCard,
    padding: "16px",
    marginBottom: "14px",
};

const sectionHeaderStyle = {
    fontSize: "13px",
    fontWeight: 500,
    color: Tokens.textSecondary,
    marginBottom: "8px",
    display: "block",
};

const rowStyle = (isLast = false) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "10px 0",
    borderBottom: isLast ? "none" : `1px solid ${Tokens.hairline}`,
});

const compactBtnStyle = {
    height: "34px",
    borderRadius: Tokens.radiusControl,
    fontSize: "13px",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};

const Settings = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const {
        visible = false,
        open = false,
        isPage = false,
        onClose = () => {},
    } = props;
    const isOpen = Boolean(isPage || visible || open);

    const { userInfo } = useSelector((state) => state.users || {});
    const { portfolios } = useSelector((state) => state.portfolios || {});
    const { assets } = useSelector((state) => state.assets || {});
    const settingsState = useSelector((state) => state.settings || {});
    const {
        lastBackup = 0,
        syncStatus = "idle",
        theme,
        currency = "USDT",
        timezone = "Asia/Kolkata",
        defaultSortBy = "currentValue",
        hideSmallBalances = false,
        hideExitedPositions = false,
        binanceTimeRange = 7,
        binanceCurrency = "USDT",
        binanceSortBy = "currentValue",
        binanceCutoffDate = "2026-01-01",
    } = settingsState;

    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );
    const [backingUp, setBackingUp] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [resyncingBinance, setResyncingBinance] = useState(false);
    const [showUpdateBinanceKeys, setShowUpdateBinanceKeys] = useState(false);
    const [confirmDisconnectBinance, setConfirmDisconnectBinance] =
        useState(false);
    const [disconnectingBinance, setDisconnectingBinance] = useState(false);
    const [checkingVersion, setCheckingVersion] = useState(false);
    const [versionStatus, setVersionStatus] = useState("idle");
    const [remoteVersion, setRemoteVersion] = useState(APP_VERSION);
    const [apiVersion, setApiVersion] = useState(null);
    const [lastCheckedTime, setLastCheckedTime] = useState(null);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const runVersionCheck = useCallback(async (showNotification = false) => {
        setCheckingVersion(true);
        try {
            const result = await checkAppVersion();
            setRemoteVersion(result.remoteVersion || APP_VERSION);
            if (result.apiVersion) {
                setApiVersion(result.apiVersion);
            }
            setLastCheckedTime(
                dayjs(result.checkedAt || Date.now()).format("HH:mm")
            );

            if (result.updateAvailable) {
                setVersionStatus("update-available");
                if (showNotification) {
                    notification.info({
                        message: "Update available",
                        description: `v${result.remoteVersion} is ready to install.`,
                    });
                }
            } else {
                setVersionStatus("up-to-date");
                if (showNotification) {
                    notification.success({
                        message: "Already up to date",
                        description: `Running v${APP_VERSION}`,
                    });
                }
            }
        } catch (err) {
            setVersionStatus("error");
            if (showNotification) {
                notification.error({
                    message: "Couldn't check for updates",
                    description: err.message || "Unable to reach update server",
                });
            }
        } finally {
            setCheckingVersion(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            runVersionCheck(false);
        }
    }, [isOpen, runVersionCheck]);

    const handleOverride = async () => {
        if (!userInfo) return;
        setBackingUp(true);
        try {
            const now = Date.now();
            await performBackup(
                "users",
                userInfo,
                portfolios,
                assets,
                {
                    lastBackup: now,
                    theme,
                    currency,
                    defaultSortBy,
                    hideSmallBalances,
                    hideExitedPositions,
                    binanceTimeRange,
                    binanceCurrency,
                    binanceSortBy,
                },
                { silent: true }
            );
            dispatch(updateLastBackup(now));
            notification.success({
                message: "Cloud backup updated",
                description:
                    "Your portfolios and preferences are saved to the cloud.",
            });
        } catch (err) {
            notification.error({
                message: "Backup failed",
                description: err.message || "Could not complete cloud backup.",
            });
        } finally {
            setBackingUp(false);
        }
    };

    const handleLocalOverride = async () => {
        if (!userInfo) return;
        setRestoring(true);
        try {
            const data = await fetchBackup("users", userInfo, { silent: true });
            if (data) {
                runWithCloudHydrationGuard(() => {
                    dispatch(updateBackedupPortfolios(data.portfolios));
                    dispatch(updateBackedupAssets(data.assets));
                    dispatch(
                        updateSettings(data.settings, { fromCloud: true })
                    );
                });
                notification.success({
                    message: "Restored from cloud backup",
                    description:
                        "Local portfolios and settings have been updated.",
                });
                onClose();
            } else {
                notification.warning({
                    message: "No cloud backup found",
                    description:
                        "No existing backup was found for this account.",
                });
            }
        } catch (err) {
            notification.error({
                message: "Restore failed",
                description:
                    err.message || "Could not restore from cloud backup.",
            });
        } finally {
            setRestoring(false);
        }
    };

    const handleLogout = async () => {
        try {
            await fetch("https://api.rajchandra.me/identity/auth/logout", {
                method: "POST",
                credentials: "include",
            });
        } catch (e) {
            // ignore - still clear local state and redirect
        }
        localStorage.clear();
        window.location.href = window.location.origin;
    };

    const handleCurrencyChange = (value) => {
        const nextCurrency = (
            typeof value === "string" ? value : value?.target?.value || "USDT"
        ).toUpperCase();
        mpTapChangeCurrency({ currency: nextCurrency });
        dispatch(
            updateSettings({
                currency: nextCurrency,
                binanceCurrency: nextCurrency,
            })
        );
    };

    const handleSortChange = (value) => {
        dispatch(
            updateSettings({
                defaultSortBy: value,
                binanceSortBy: value,
            })
        );
    };

    const handleTimeRangeChange = (value) => {
        dispatch(
            updateSettings({
                binanceTimeRange: value,
            })
        );
    };

    const handleTimezoneChange = (value) => {
        dispatch(
            updateSettings({
                timezone: value,
            })
        );
    };

    const handleBinanceResync = async () => {
        setResyncingBinance(true);
        try {
            const api = `${process.env.REACT_APP_API}/binance/report-card/sync`;
            await Axios.post(api, null, { withCredentials: true });
            notification.success({
                message: "Binance ledger sync started",
                description:
                    "Re-scanning trades, Auto-Invest, and Convert history in the background.",
            });
        } catch (err) {
            if (err.response?.status === 401) {
                notification.info({
                    message: "Sign in required",
                    description:
                        "Please sign in to sync your Binance trade ledger.",
                });
            } else {
                notification.error({
                    message: "Could not start ledger sync",
                    description:
                        err.response?.data?.message ||
                        "Verify your Binance API keys and try again.",
                });
            }
        } finally {
            setResyncingBinance(false);
        }
    };

    const handleBinanceDisconnect = async () => {
        setDisconnectingBinance(true);
        try {
            const api = `${process.env.REACT_APP_API}/binance/auth/disconnect`;
            await Axios.delete(api, { withCredentials: true });
            localStorage.removeItem("binance_wallet_summary_cache");
            setConfirmDisconnectBinance(false);
            notification.success({
                message: "Binance disconnected",
                description: "Your API credentials have been removed.",
            });
        } catch (err) {
            notification.error({
                message: "Could not disconnect Binance",
                description: err.message || "Please try again.",
            });
        } finally {
            setDisconnectingBinance(false);
        }
    };

    const handleApplyUpdate = async () => {
        notification.info({
            message: "Updating application",
            description: `Applying v${
                remoteVersion || APP_VERSION
            } and refreshing cache...`,
        });
        await applyAppUpdate();
    };

    const handleClearCache = async () => {
        notification.info({
            message: "Clearing cache",
            description: "Purging service worker and cached assets...",
        });
        await clearCacheAndReload();
    };

    const fullName =
        userInfo?.name?.fullName ||
        (typeof userInfo?.name === "string" ? userInfo.name : null) ||
        "Guest workspace";
    const userEmail =
        userInfo?.email ||
        (userInfo
            ? "Synced with Google SSO"
            : "Sign in to sync portfolios across devices");
    const avatarInitial = (fullName || "G").trim().charAt(0).toUpperCase();

    const settingsBody = (
        <div
            data-testid="settings-content-body"
            style={{
                maxWidth: Tokens.containerMaxWidth,
                margin: "0 auto",
                width: "100%",
            }}
        >
            {/* 1. Author / Account Identity Card */}
            <div data-testid="settings-identity-card" style={cardStyle}>
                <span style={sectionHeaderStyle}>Account</span>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        {userInfo?.picture ? (
                            <img
                                src={userInfo.picture}
                                alt={fullName}
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: `1px solid ${Tokens.hairlineStrong}`,
                                    flexShrink: 0,
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    backgroundColor: Tokens.raised,
                                    border: `1px solid ${Tokens.hairlineStrong}`,
                                    color: Tokens.textPrimary,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 600,
                                    fontSize: "15px",
                                    flexShrink: 0,
                                }}
                            >
                                {avatarInitial}
                            </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {fullName}
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {userEmail}
                            </div>
                        </div>
                    </div>

                    {userInfo ? (
                        <Button
                            data-testid="settings-logout-btn"
                            type="default"
                            icon={<LogoutOutlined />}
                            style={{
                                ...compactBtnStyle,
                                backgroundColor: Tokens.raised,
                                color: Tokens.textPrimary,
                                border: `1px solid ${Tokens.hairline}`,
                            }}
                            onClick={() => {
                                mpTapLogoutCTA();
                                handleLogout();
                            }}
                        >
                            Sign out
                        </Button>
                    ) : (
                        <Button
                            data-testid="settings-login-btn"
                            type="primary"
                            icon={<LoginOutlined />}
                            style={compactBtnStyle}
                            onClick={() => {
                                mpTapLoginCTA();
                                const returnTo = `${window.location.origin}?login=1`;
                                window.location.href = `${authApp}?returnTo=${encodeURIComponent(
                                    returnTo
                                )}`;
                            }}
                        >
                            Log in
                        </Button>
                    )}
                </div>

                {userInfo && (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                            marginTop: "12px",
                            paddingTop: "10px",
                            borderTop: `1px solid ${Tokens.hairline}`,
                        }}
                    >
                        <Tag
                            icon={<SafetyCertificateOutlined />}
                            style={{
                                margin: 0,
                                backgroundColor: Tokens.raised,
                                color: Tokens.textSecondary,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusTag,
                                fontSize: "11px",
                                padding: "1px 8px",
                            }}
                        >
                            Google SSO
                        </Tag>
                        <Tag
                            data-testid="settings-auto-sync-tag"
                            style={{
                                margin: 0,
                                backgroundColor:
                                    syncStatus === "syncing"
                                        ? Tokens.accentSubtle
                                        : Tokens.positiveBg,
                                color:
                                    syncStatus === "syncing"
                                        ? Tokens.accent
                                        : Tokens.positive,
                                border: `1px solid ${
                                    syncStatus === "syncing"
                                        ? Tokens.accentBorder
                                        : Tokens.positiveBorder
                                }`,
                                borderRadius: Tokens.radiusTag,
                                fontSize: "11px",
                                padding: "1px 8px",
                            }}
                        >
                            {(() => {
                                if (syncStatus === "syncing") {
                                    return "Auto-syncing...";
                                }
                                if (Number(lastBackup) > 0) {
                                    return `Auto-sync · Synced ${dayjs(
                                        lastBackup
                                    ).fromNow()}`;
                                }
                                return "Auto-sync active";
                            })()}
                        </Tag>
                    </div>
                )}
            </div>

            {/* 2. Default Preferences Card */}
            <div data-testid="settings-preferences-card" style={cardStyle}>
                <span style={sectionHeaderStyle}>Default preferences</span>

                {/* Row 0: Appearance / Theme switcher */}
                <div style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Theme
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Light default, Dark, or Auto system preference
                        </div>
                    </div>
                    <ThemeSwitcher testIdPrefix="settings" />
                </div>

                {/* Row 1: Base currency */}
                <div style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Base currency
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Default currency for manual and exchange portfolios
                        </div>
                    </div>
                    <Select
                        data-testid="settings-currency-select"
                        value={(currency || "USDT").toUpperCase()}
                        style={{ width: 130 }}
                        onChange={handleCurrencyChange}
                        options={CURRENCY_OPTIONS}
                    />
                </div>

                {/* Row 1b: Timezone */}
                <div style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Timezone
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Display timezone for ledger &amp; tradebook
                            timestamps
                        </div>
                    </div>
                    <Select
                        data-testid="settings-timezone-select"
                        value={timezone || "Asia/Kolkata"}
                        style={{ width: 185 }}
                        onChange={handleTimezoneChange}
                        options={TIMEZONE_OPTIONS}
                    />
                </div>

                {/* Row 2: Default sort order */}
                <div style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Default sort order
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Initial sort order for coins and holdings
                        </div>
                    </div>
                    <Select
                        data-testid="settings-sort-select"
                        value={defaultSortBy || binanceSortBy || "currentValue"}
                        style={{ width: 165 }}
                        onChange={handleSortChange}
                        options={SORT_OPTIONS}
                    />
                </div>

                {/* Row 3: Binance time horizon */}
                <div style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Binance time horizon
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Default lookback window for exchange analytics
                        </div>
                    </div>
                    <Select
                        data-testid="settings-timerange-select"
                        value={binanceTimeRange || 7}
                        style={{ width: 130 }}
                        onChange={handleTimeRangeChange}
                        options={TIME_RANGE_OPTIONS}
                    />
                </div>

                {/* Row 3b: P&L baseline (cutoff date) */}
                <div style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            P&amp;L baseline
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Cost-basis &amp; P&amp;L cutoff across exchange
                            views
                        </div>
                    </div>
                    <Select
                        data-testid="settings-cutoff-select"
                        value={binanceCutoffDate || "2026-01-01"}
                        style={{ width: 150 }}
                        onChange={(val) =>
                            dispatch(
                                updateSettings({
                                    binanceCutoffDate: val,
                                })
                            )
                        }
                        options={CUTOFF_PRESET_OPTIONS}
                    />
                </div>

                {/* Row 4: Hide small balances */}
                <div style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Hide small balances
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Hide dust holdings under $1.00
                        </div>
                    </div>
                    <Switch
                        data-testid="settings-hide-small-switch"
                        checked={Boolean(hideSmallBalances)}
                        onChange={(val) =>
                            dispatch(
                                updateSettings({
                                    hideSmallBalances: val,
                                })
                            )
                        }
                    />
                </div>

                {/* Row 5: Hide exited positions */}
                <div style={rowStyle(true)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Hide exited positions
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Hide closed positions with 0 balance in manual
                            portfolios
                        </div>
                    </div>
                    <Switch
                        data-testid="settings-hide-exited-switch"
                        checked={Boolean(hideExitedPositions)}
                        onChange={(val) =>
                            dispatch(
                                updateSettings({
                                    hideExitedPositions: val,
                                })
                            )
                        }
                    />
                </div>
            </div>

            {/* 3. Cloud Backup & Exchange Sync Card */}
            <div data-testid="settings-sync-card" style={cardStyle}>
                <span style={sectionHeaderStyle}>
                    Cloud backup &amp; exchange sync
                </span>

                {userInfo ? (
                    <React.Fragment>
                        <div style={rowStyle(false)}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                    }}
                                >
                                    Backup to cloud
                                </div>
                                <div
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "12px",
                                    }}
                                >
                                    Save local portfolios, transactions, and
                                    settings to cloud storage
                                </div>
                            </div>
                            <Button
                                data-testid="settings-backup-now-btn"
                                type="default"
                                icon={<CloudUploadOutlined />}
                                loading={backingUp}
                                style={{
                                    ...compactBtnStyle,
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textPrimary,
                                    border: `1px solid ${Tokens.hairline}`,
                                }}
                                onClick={() => {
                                    mpTapOverrideCTA();
                                    handleOverride();
                                }}
                            >
                                Backup now
                            </Button>
                        </div>

                        <div style={rowStyle(false)}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                    }}
                                >
                                    Restore from cloud
                                </div>
                                <div
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "12px",
                                    }}
                                >
                                    Replace local state with your latest cloud
                                    backup
                                </div>
                            </div>
                            <Button
                                data-testid="settings-restore-btn"
                                type="default"
                                icon={<CloudDownloadOutlined />}
                                loading={restoring}
                                style={{
                                    ...compactBtnStyle,
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textPrimary,
                                    border: `1px solid ${Tokens.hairline}`,
                                }}
                                onClick={() => {
                                    mpTapLocalOverrideCTA();
                                    handleLocalOverride();
                                }}
                            >
                                Restore
                            </Button>
                        </div>

                        <div style={rowStyle(false)}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                    }}
                                >
                                    Binance trade ledger
                                </div>
                                <div
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "12px",
                                    }}
                                >
                                    Re-scan historical Spot, Auto-Invest, and
                                    Convert trades
                                </div>
                            </div>
                            <Button
                                data-testid="settings-binance-resync-btn"
                                type="default"
                                icon={
                                    <ReloadOutlined spin={resyncingBinance} />
                                }
                                loading={resyncingBinance}
                                style={{
                                    ...compactBtnStyle,
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textPrimary,
                                    border: `1px solid ${Tokens.hairline}`,
                                }}
                                onClick={handleBinanceResync}
                            >
                                Re-sync ledger
                            </Button>
                        </div>

                        <div style={rowStyle(false)}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                    }}
                                >
                                    Binance API credentials
                                </div>
                                <div
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "12px",
                                    }}
                                >
                                    Connect or rotate read-only Binance API keys
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    flexWrap: "wrap",
                                }}
                            >
                                <Button
                                    data-testid="settings-binance-keys-btn"
                                    type="default"
                                    icon={<KeyOutlined />}
                                    style={{
                                        ...compactBtnStyle,
                                        backgroundColor: Tokens.raised,
                                        color: Tokens.textPrimary,
                                        border: `1px solid ${Tokens.hairline}`,
                                    }}
                                    onClick={() =>
                                        setShowUpdateBinanceKeys(true)
                                    }
                                >
                                    API keys
                                </Button>
                                {confirmDisconnectBinance ? (
                                    <React.Fragment>
                                        <Button
                                            data-testid="settings-confirm-disconnect-binance-btn"
                                            danger
                                            type="primary"
                                            loading={disconnectingBinance}
                                            style={compactBtnStyle}
                                            onClick={handleBinanceDisconnect}
                                        >
                                            Confirm
                                        </Button>
                                        <Button
                                            data-testid="settings-cancel-disconnect-binance-btn"
                                            type="default"
                                            style={compactBtnStyle}
                                            onClick={() =>
                                                setConfirmDisconnectBinance(
                                                    false
                                                )
                                            }
                                        >
                                            Cancel
                                        </Button>
                                    </React.Fragment>
                                ) : (
                                    <Button
                                        data-testid="settings-disconnect-binance-btn"
                                        danger
                                        type="text"
                                        icon={<DisconnectOutlined />}
                                        style={compactBtnStyle}
                                        onClick={() =>
                                            setConfirmDisconnectBinance(true)
                                        }
                                        title="Disconnect Binance"
                                    />
                                )}
                            </div>
                        </div>
                    </React.Fragment>
                ) : (
                    <div
                        style={{
                            padding: "8px 0 12px",
                            borderBottom: `1px solid ${Tokens.hairline}`,
                        }}
                    >
                        <LoginWall
                            compact
                            testId="settings-guest-sync-prompt"
                            title="Sign in to enable Binance sync & cloud backup"
                            description="Connect your account to link Binance API keys, re-sync your trade ledger, and back up manual portfolios."
                            returnPath="/settings"
                        />
                    </div>
                )}

                <div style={rowStyle(true)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Binance portfolio
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            View live exchange holdings and allocation
                        </div>
                    </div>
                    <Button
                        data-testid="settings-open-binance-btn"
                        type="default"
                        icon={<SwapOutlined />}
                        style={{
                            ...compactBtnStyle,
                            backgroundColor: Tokens.raised,
                            color: Tokens.textPrimary,
                            border: `1px solid ${Tokens.hairline}`,
                        }}
                        onClick={() => {
                            onClose();
                            if (history && typeof history.push === "function") {
                                history.push("/imported/binance");
                            }
                        }}
                    >
                        Open Binance
                    </Button>
                </div>
            </div>

            {/* 4. Application Updates & System Telemetry Card */}
            <div
                data-testid="settings-system-card"
                style={{ ...cardStyle, marginBottom: 0 }}
            >
                <span style={sectionHeaderStyle}>Application &amp; system</span>

                {/* Row 1: Application updates */}
                <div data-testid="settings-version-row" style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Application updates
                        </div>
                        <div
                            style={{
                                fontSize: "12px",
                                marginTop: "2px",
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {checkingVersion && (
                                <span
                                    style={{
                                        color: Tokens.textSecondary,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <Spin size="small" /> Checking for
                                    updates...
                                </span>
                            )}
                            {!checkingVersion &&
                                versionStatus === "update-available" && (
                                    <span
                                        style={{
                                            color: Tokens.accent,
                                            fontWeight: 500,
                                        }}
                                    >
                                        <ArrowUpOutlined /> New version ready: v
                                        {remoteVersion} (current: v{APP_VERSION}
                                        )
                                    </span>
                                )}
                            {!checkingVersion &&
                                versionStatus === "up-to-date" && (
                                    <span style={{ color: Tokens.positive }}>
                                        <CheckCircleOutlined /> Up to date (v
                                        {APP_VERSION})
                                        {lastCheckedTime
                                            ? ` · Checked ${lastCheckedTime}`
                                            : ""}
                                    </span>
                                )}
                            {!checkingVersion && versionStatus === "error" && (
                                <span style={{ color: Tokens.negative }}>
                                    Could not check for updates · Running v
                                    {APP_VERSION}
                                </span>
                            )}
                            {!checkingVersion && versionStatus === "idle" && (
                                <span
                                    style={{
                                        color: Tokens.textSecondary,
                                    }}
                                >
                                    Current build: v{APP_VERSION}
                                </span>
                            )}
                        </div>
                    </div>

                    {versionStatus === "update-available" ? (
                        <Button
                            data-testid="settings-update-now-btn"
                            type="primary"
                            icon={<CloudDownloadOutlined />}
                            onClick={handleApplyUpdate}
                            style={compactBtnStyle}
                        >
                            Update now
                        </Button>
                    ) : (
                        <Button
                            data-testid="settings-check-updates-btn"
                            type="default"
                            icon={<ReloadOutlined spin={checkingVersion} />}
                            loading={checkingVersion}
                            onClick={() => runVersionCheck(true)}
                            style={{
                                ...compactBtnStyle,
                                backgroundColor: Tokens.raised,
                                color: Tokens.textPrimary,
                                border: `1px solid ${Tokens.hairline}`,
                            }}
                        >
                            Check for updates
                        </Button>
                    )}
                </div>

                {/* Row 2: Local cache & service worker */}
                <div style={rowStyle(false)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "13px",
                                fontWeight: 500,
                            }}
                        >
                            Local cache &amp; service worker
                        </div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Purge cached assets and reload the application
                        </div>
                    </div>
                    <Button
                        data-testid="settings-clear-cache-btn"
                        type="default"
                        icon={<DeleteOutlined />}
                        onClick={handleClearCache}
                        style={{
                            ...compactBtnStyle,
                            backgroundColor: Tokens.raised,
                            color: Tokens.textPrimary,
                            border: `1px solid ${Tokens.hairline}`,
                        }}
                    >
                        Clear cache
                    </Button>
                </div>

                {/* Row 3: Build & API telemetry */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                        paddingTop: "10px",
                        color: Tokens.textMuted,
                        fontSize: "11px",
                        fontFamily: Tokens.fontMono,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    <Text
                        style={{
                            color: Tokens.textMuted,
                            fontSize: "11px",
                            fontFamily: Tokens.fontMono,
                        }}
                    >
                        Frontend: v{APP_VERSION} &middot; API:{" "}
                        {apiVersion ? `v${apiVersion}` : "—"}
                    </Text>
                    <a
                        href="https://www.coingecko.com/"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            color: Tokens.textMuted,
                            textDecoration: "none",
                        }}
                    >
                        <span>Market data by CoinGecko</span>
                        <img
                            src={CoinGeckoLogo}
                            alt="CoinGecko"
                            height="18px"
                        />
                    </a>
                </div>
            </div>
            <AddBinance
                showConnectBinanceDrawer={showUpdateBinanceKeys}
                onClose={() => setShowUpdateBinanceKeys(false)}
                getAssetInfo={() => {}}
            />
        </div>
    );

    if (isPage) {
        return (
            <div data-testid="settings-page-root">
                <AppShell
                    activeTab="settings"
                    headerStyle={{
                        backgroundColor: Tokens.bg,
                        borderBottom: `1px solid ${Tokens.hairline}`,
                        padding: "16px 24px",
                    }}
                    bodyStyle={{
                        backgroundColor: Tokens.bg,
                        padding: "20px 16px 88px",
                    }}
                    title={
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                maxWidth: Tokens.containerMaxWidth,
                                margin: "0 auto",
                                width: "100%",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                <Button
                                    type="text"
                                    icon={
                                        <ArrowLeftOutlined
                                            style={{
                                                color: Tokens.textSecondary,
                                                fontSize: "16px",
                                            }}
                                        />
                                    }
                                    onClick={() => {
                                        if (
                                            history &&
                                            typeof history.push === "function"
                                        ) {
                                            history.push("/portfolios");
                                        }
                                    }}
                                    title="Back to portfolios"
                                    style={{
                                        padding: 0,
                                        width: "32px",
                                        height: "32px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                />
                                <Title
                                    level={4}
                                    style={{
                                        margin: 0,
                                        color: Tokens.textPrimary,
                                        fontWeight: 500,
                                        fontSize: "18px",
                                    }}
                                >
                                    Profile &amp; Settings
                                </Title>
                                <AutoSyncBadge />
                            </div>
                        </div>
                    }
                >
                    {settingsBody}
                </AppShell>
            </div>
        );
    }

    return (
        <Drawer
            title={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                    }}
                >
                    <Title
                        level={4}
                        style={{
                            margin: 0,
                            color: Tokens.textPrimary,
                            fontWeight: 600,
                            fontSize: "16px",
                            letterSpacing: "-0.01em",
                        }}
                    >
                        Settings &amp; Preferences
                    </Title>
                </div>
            }
            placement={isMobile ? "bottom" : "right"}
            closable
            push={false}
            width={isMobile ? "100%" : 480}
            height={isMobile ? "88%" : "100%"}
            onClose={onClose}
            closeIcon={
                <CloseOutlined style={{ color: Tokens.textSecondary }} />
            }
            open={isOpen}
            bodyStyle={{
                backgroundColor: Tokens.bg,
                padding: "16px",
                color: Tokens.textPrimary,
            }}
            headerStyle={{
                backgroundColor: Tokens.bg,
                borderBottom: `1px solid ${Tokens.hairline}`,
                padding: "14px 16px",
            }}
        >
            {settingsBody}
        </Drawer>
    );
};

export default Settings;
